import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import {
    FrequencyUnits,
    IIndicatorTimeSeriesData,
    IIndicatorTimeSeriesResponse,
    IndicatorTimeSeriesRequest,
} from '@kcftech/prometheus-client';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useFeatureFlag } from 'contexts/configcat/useFeatureFlag';
import { dataPlatformClient, sdClient } from 'contexts/SDKClientContext';
import { getUnitsRequest } from 'custom_hooks/data_hooks/burst_data/useBurstData';
import { type IndicatorSource } from 'custom_hooks/data_hooks/indicator/shared';
import { useCurrentUserInfo } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { useDebounce } from 'custom_hooks/useDebounce';
import { useStickyNumber } from 'custom_hooks/useStickyNumber';
import { convertChartRangeToDateRange } from 'pages/workbench/shared';
import {
    calcTimeSeriesDownsamplingSeconds,
    calcTimeSeriesMaxPoints,
    dateRangesAreEqual,
    getDataPlatformTimeSeriesChartPrimaryUnit,
    getIndicatorLineStyle,
    getTimeSeriesChartPrimaryUnit,
    getTimeSeriesIndicatorUnits,
    transformTimeSeriesData,
} from 'pages/workbench/workbench_tabs/trend/timeseries/shared';
import { TrendIndicator } from 'pages/workbench/workbench_tabs/trend/useTrendIndicators';
import { useCallback, useMemo } from 'react';
import { ChartRange, TimeSeriesData, TimeSeriesDataPoint } from 'shared/chart/core/types';
import { isChartRangeEmpty } from 'shared/chart/shared/data';
import { queryClient } from 'utils/query';
import { max, min } from 'utils/reduction';
import { Modify } from 'utils/types';

const QueryKey = 'timeseries';
const StickWidthThresholdPx = 50;

export function invalidateTrendTimeSeriesData() {
    queryClient.invalidateQueries({
        queryKey: [QueryKey],
    });
}

type BuildTimeSeriesDataOptions = Readonly<{
    results: IIndicatorTimeSeriesResponse[];
    indicatorIdToIndicatorMap: Map<string, TrendIndicator>;
    request: IndicatorTimeSeriesRequest;
    primaryIndicatorUnit: string;
    showMinMax: boolean;
}>;

const buildTimeSeriesData = ({
    results,
    indicatorIdToIndicatorMap,
    request,
    primaryIndicatorUnit,
    showMinMax,
}: BuildTimeSeriesDataOptions): TimeSeriesData[] =>
    results.map<TimeSeriesData>((series) => {
        const trendIndicator = indicatorIdToIndicatorMap.get(series.IndicatorId);

        if (!trendIndicator) {
            throw new Error(
                `Series in query results not found in selected indicators: ${series.IndicatorId}`,
            );
        }

        const { color, indicator } = trendIndicator;

        const data: TimeSeriesData = {
            axis:
                getTimeSeriesIndicatorUnits(indicator) === primaryIndicatorUnit
                    ? 'left'
                    : 'right',
            seriesUnit: series.DisplayUnits || getTimeSeriesIndicatorUnits(indicator),
            seriesMinY: series.OverallMinY,
            seriesMaxY: series.OverallMaxY,
            dataPoints: transformTimeSeriesData({
                indicator,
                request,
                data: series.IndicatorData,
            }).map<TimeSeriesDataPoint>(({ X, AvgY, MinY, MaxY }) => ({
                x: X,
                y: AvgY ?? 0,
                minY: MinY ?? AvgY ?? 0,
                maxY: MaxY ?? AvgY ?? 0,
            })),
            seriesId: series.IndicatorId,
            lineStyles: {
                color,
                curveStyle: getIndicatorLineStyle(indicator),
            },
            showMinMax,
        };

        return data;
    });

const buildDataPlatformTimeSeriesData = ({
    results,
    indicatorIdToIndicatorMap,
    request,
    primaryIndicatorUnit,
    showMinMax,
}: Modify<
    BuildTimeSeriesDataOptions,
    { results: DataPlatformApi.IndicatorDataResponse[] }
>): TimeSeriesData[] =>
    results.map<TimeSeriesData>((series) => {
        const trendIndicator = indicatorIdToIndicatorMap.get(series.id);

        if (!trendIndicator) {
            throw new Error(
                `Series in query results not found in selected indicators: ${series.id}`,
            );
        }

        const { color, indicator } = trendIndicator;

        const data: TimeSeriesData = {
            axis:
                getTimeSeriesIndicatorUnits(indicator) === primaryIndicatorUnit
                    ? 'left'
                    : 'right',
            seriesUnit: getTimeSeriesIndicatorUnits(indicator),
            seriesMinY: min(series.data.values),
            seriesMaxY: max(series.data.values),
            dataPoints: transformTimeSeriesData({
                indicator,
                request,
                data: series.data.timestamps.map<IIndicatorTimeSeriesData>(
                    (dataPoint, index) => ({
                        X: dataPoint,
                        AvgY: series.data.values[index],
                        MinY:
                            series.data.statistics?.[
                                DataPlatformApi.DownsamplingStatistic.Min
                            ]?.[index] ?? undefined,
                        MaxY:
                            series.data.statistics?.[
                                DataPlatformApi.DownsamplingStatistic.Max
                            ]?.[index] ?? undefined,
                    }),
                ),
            }).map<TimeSeriesDataPoint>(({ X, AvgY, MinY, MaxY }) => ({
                x: X,
                y: AvgY ?? 0,
                minY: MinY ?? AvgY ?? 0,
                maxY: MaxY ?? AvgY ?? 0,
            })),
            seriesId: series.id,
            lineStyles: {
                color,
                curveStyle: getIndicatorLineStyle(indicator),
            },
            showMinMax,
        };

        return data;
    });

const getUnitForIndicator = (
    indicator: TrendIndicator,
    unitsRequest: ReturnType<typeof getUnitsRequest>,
) => {
    if (!indicator.indicator.PhysicalQuantity || indicator.indicator.HasArbitraryUnits) {
        return undefined;
    }

    return unitsRequest[indicator.indicator.PhysicalQuantity];
};

export type UseTrendTimeSeriesDataOptions = Readonly<{
    trendIndicators: TrendIndicator[];
    chartWidth: number;
    range?: ChartRange;
    zoomRange?: ChartRange;
    showMinMax: boolean;
    runningSpeedUnit: FrequencyUnits;
    downsamplingMethod: DataPlatformApi.DownsamplingMethod;
    /**
     * the backend all `trendIndicators` belong to; defaults to the one the
     * `timeSeriesCutoverPhase2` flag selects
     */
    source?: IndicatorSource;
}>;

export function useTrendTimeSeriesData({
    trendIndicators,
    chartWidth,
    range,
    zoomRange,
    showMinMax,
    runningSpeedUnit,
    downsamplingMethod,
    source,
}: UseTrendTimeSeriesDataOptions) {
    const cutoverFlag = useFeatureFlag('timeSeriesCutoverPhase2');
    // named for the flag, but an explicit `source` takes its place
    const timeSeriesCutoverPhase2 = source
        ? source === 'data-platform'
        : cutoverFlag.value;
    const isLoadingTimeSeriesCutoverPhase2 = !source && cutoverFlag.loading;

    const { data: currentUser } = useCurrentUserInfo();
    const unitsRequest = getUnitsRequest(currentUser);

    const dateRange = useMemo(
        () =>
            range && !isChartRangeEmpty(range)
                ? convertChartRangeToDateRange(range)
                : undefined,
        [range],
    );

    const zoomDateRange = useMemo(
        () =>
            zoomRange && !isChartRangeEmpty(zoomRange)
                ? convertChartRangeToDateRange(zoomRange)
                : undefined,
        [zoomRange],
    );

    const finalZoomDateRange = useMemo(
        () =>
            !dateRange || !zoomDateRange || dateRangesAreEqual(dateRange, zoomDateRange)
                ? undefined
                : zoomDateRange,
        [dateRange, zoomDateRange],
    );

    const debouncedDateRange = useDebounce(dateRange, 500);
    const debouncedZoomDateRange = useDebounce(finalZoomDateRange, 500);

    if (!debouncedDateRange) {
        throw new Error('Missing query date range');
    }

    const indicatorIdToIndicatorMap = useMemo(
        () =>
            new Map(
                trendIndicators.map((indicator) => [indicator.indicatorId, indicator]),
            ),
        [trendIndicators],
    );

    const indicatorIds = useMemo(
        () => new Set(trendIndicators.map(({ indicatorId }) => indicatorId)),
        [trendIndicators],
    );

    const stickyWidth = useStickyNumber(chartWidth, StickWidthThresholdPx);

    const maxPoints = useMemo(
        () => calcTimeSeriesMaxPoints(indicatorIds.size, stickyWidth),
        [stickyWidth, indicatorIds.size],
    );

    const downsamplingIntervalSeconds = useMemo(
        () =>
            calcTimeSeriesDownsamplingSeconds(
                debouncedDateRange,
                stickyWidth,
                indicatorIds.size,
            ),
        [debouncedDateRange, stickyWidth, indicatorIds.size],
    );

    // If running speed unit is orders, request Hz instead because running speed is always 1 order
    const runningSpeedUnitForRequest = useMemo(
        () =>
            runningSpeedUnit === FrequencyUnits.Order
                ? FrequencyUnits.Hertz
                : runningSpeedUnit,
        [runningSpeedUnit],
    );

    const request = useMemo(
        () =>
            new IndicatorTimeSeriesRequest({
                AddFirstAndLastPoint: true,
                IncludeAvgData: true,
                IncludeMinAndMaxData: showMinMax,
                IndicatorIds: Array.from(indicatorIds.keys()),
                MaxPointsReturned: maxPoints,
                StartDate: debouncedDateRange.startDate,
                EndDate: debouncedDateRange.endDate,
                RunningSpeedUnits: runningSpeedUnitForRequest,
            }),
        [
            indicatorIds,
            maxPoints,
            runningSpeedUnitForRequest,
            showMinMax,
            debouncedDateRange,
        ],
    );

    const dataPlatformRequest = useMemo<DataPlatformApi.IndicatorsDataRequest>(() => {
        return {
            startTime: request.StartDate.valueOf(),
            endTime: request.EndDate.valueOf(),
            indicators: trendIndicators.map((ind) => ({
                id: ind.indicatorId,
                unit: getUnitForIndicator(ind, unitsRequest) ?? undefined,
            })),
            downsampling: downsamplingIntervalSeconds
                ? {
                      method: downsamplingMethod,
                      binSizeSeconds: downsamplingIntervalSeconds,
                      statistics: showMinMax
                          ? [
                                DataPlatformApi.DownsamplingStatistic.Min,
                                DataPlatformApi.DownsamplingStatistic.Max,
                            ]
                          : undefined,
                  }
                : undefined,
        };
    }, [
        request.StartDate,
        request.EndDate,
        trendIndicators,
        downsamplingMethod,
        downsamplingIntervalSeconds,
        showMinMax,
        unitsRequest,
    ]);

    const zoomedRequest = useMemo(
        () =>
            debouncedZoomDateRange
                ? new IndicatorTimeSeriesRequest({
                      AddFirstAndLastPoint: true,
                      IncludeAvgData: true,
                      IncludeMinAndMaxData: showMinMax,
                      IndicatorIds: Array.from(indicatorIds.keys()),
                      MaxPointsReturned: maxPoints,
                      StartDate: debouncedZoomDateRange.startDate,
                      EndDate: debouncedZoomDateRange.endDate,
                      RunningSpeedUnits: runningSpeedUnitForRequest,
                  })
                : undefined,
        [
            indicatorIds,
            maxPoints,
            runningSpeedUnitForRequest,
            showMinMax,
            debouncedZoomDateRange,
        ],
    );

    const dataPlatformZoomedRequest = useMemo(() => {
        return zoomedRequest
            ? {
                  ...dataPlatformRequest,
                  startTime: zoomedRequest.StartDate.valueOf(),
                  endTime: zoomedRequest.EndDate.valueOf(),
              }
            : undefined;
    }, [dataPlatformRequest, zoomedRequest]);

    const query = useQuery({
        queryKey: [QueryKey, request, timeSeriesCutoverPhase2],
        queryFn: () => sdClient.getIndicatorTimeSeriesData({ body: request }),
        placeholderData: (previousData, previousQuery) =>
            // If the previous query was for a different cutover phase, we don't want to use its data as placeholder data.
            previousQuery?.queryKey[2] !== timeSeriesCutoverPhase2
                ? undefined
                : previousData,
        enabled: !isLoadingTimeSeriesCutoverPhase2 && !timeSeriesCutoverPhase2,
    });

    const dataPlatformQuery = useQuery({
        queryKey: [QueryKey, dataPlatformRequest, timeSeriesCutoverPhase2],
        queryFn: () =>
            dataPlatformClient.indicators.getIndicatorData(dataPlatformRequest),
        placeholderData: (previousData, previousQuery) =>
            // If the previous query was for a different cutover phase, we don't want to use its data as placeholder data.
            previousQuery?.queryKey[2] !== timeSeriesCutoverPhase2
                ? undefined
                : previousData,
        enabled: !isLoadingTimeSeriesCutoverPhase2 && timeSeriesCutoverPhase2,
    });

    const zoomedQuery = useQuery({
        queryKey: [QueryKey, zoomedRequest, timeSeriesCutoverPhase2],
        queryFn: zoomedRequest
            ? () => sdClient.getIndicatorTimeSeriesData({ body: zoomedRequest })
            : skipToken,
        placeholderData: (previousData, previousQuery) =>
            // If the previous query was for a different cutover phase, we don't want to use its data as placeholder data.
            previousQuery?.queryKey[2] !== timeSeriesCutoverPhase2
                ? undefined
                : previousData,
        enabled: !isLoadingTimeSeriesCutoverPhase2 && !timeSeriesCutoverPhase2,
    });

    const zoomedDataPlatformQuery = useQuery({
        queryKey: [QueryKey, dataPlatformZoomedRequest, timeSeriesCutoverPhase2],
        queryFn: dataPlatformZoomedRequest
            ? () =>
                  dataPlatformClient.indicators.getIndicatorData(
                      dataPlatformZoomedRequest,
                  )
            : skipToken,
        placeholderData: (previousData, previousQuery) =>
            // If the previous query was for a different cutover phase, we don't want to use its data as placeholder data.
            previousQuery?.queryKey[2] !== timeSeriesCutoverPhase2
                ? undefined
                : previousData,
        enabled: !isLoadingTimeSeriesCutoverPhase2 && timeSeriesCutoverPhase2,
    });

    const { queryData, zoomedQueryData } = useMemo(() => {
        if (timeSeriesCutoverPhase2 && dataPlatformQuery.data) {
            const primaryIndicatorUnit = getDataPlatformTimeSeriesChartPrimaryUnit(
                trendIndicators,
                dataPlatformQuery.data,
            );

            const mappedQueryData = buildDataPlatformTimeSeriesData({
                primaryIndicatorUnit,
                results: dataPlatformQuery.data.filter((datum) =>
                    indicatorIds.has(datum.id),
                ),
                request,
                showMinMax,
                indicatorIdToIndicatorMap,
            });

            return {
                queryData: mappedQueryData,
                zoomedQueryData: zoomedDataPlatformQuery.data
                    ? buildDataPlatformTimeSeriesData({
                          primaryIndicatorUnit,
                          results: zoomedDataPlatformQuery.data.filter((datum) =>
                              indicatorIds.has(datum.id),
                          ),
                          request,
                          showMinMax,
                          indicatorIdToIndicatorMap,
                      })
                    : mappedQueryData,
            };
        }

        if (query.data) {
            const primaryIndicatorUnit = getTimeSeriesChartPrimaryUnit(
                trendIndicators,
                query.data,
            );

            const trendIndicatorsIds = new Set(
                trendIndicators.map((indicator) => indicator.indicatorId),
            );

            const mappedQueryData = buildTimeSeriesData({
                primaryIndicatorUnit,
                results: query.data.filter((datum) =>
                    trendIndicatorsIds.has(datum.IndicatorId),
                ),
                request,
                showMinMax,
                indicatorIdToIndicatorMap,
            });
            return {
                queryData: mappedQueryData,
                zoomedQueryData: zoomedQuery.data
                    ? buildTimeSeriesData({
                          primaryIndicatorUnit,
                          results: zoomedQuery.data.filter((datum) =>
                              trendIndicatorsIds.has(datum.IndicatorId),
                          ),
                          request,
                          showMinMax,
                          indicatorIdToIndicatorMap,
                      })
                    : mappedQueryData,
            };
        }
        return {};
    }, [
        timeSeriesCutoverPhase2,
        dataPlatformQuery.data,
        query.data,
        trendIndicators,
        request,
        showMinMax,
        indicatorIdToIndicatorMap,
        zoomedDataPlatformQuery.data,
        indicatorIds,
        zoomedQuery.data,
    ]);

    const refetch = useCallback(() => {
        if (!timeSeriesCutoverPhase2) {
            query.refetch();
            zoomedQuery.refetch();
        } else {
            dataPlatformQuery.refetch();
            zoomedDataPlatformQuery.refetch();
        }
    }, [
        dataPlatformQuery,
        query,
        timeSeriesCutoverPhase2,
        zoomedDataPlatformQuery,
        zoomedQuery,
    ]);

    return {
        timeSeriesData: queryData,
        zoomedTimeSeriesData:
            zoomedRequest && zoomedQueryData ? zoomedQueryData : queryData,
        isLoading: timeSeriesCutoverPhase2
            ? dataPlatformQuery.isFetching || zoomedDataPlatformQuery.isFetching
            : query.isFetching || zoomedQuery.isFetching,
        isError: timeSeriesCutoverPhase2
            ? dataPlatformQuery.isError || zoomedDataPlatformQuery.isError
            : query.isError || zoomedQuery.isError,
        refetch,
    };
}
